import React, {
  useEffect,
  useState,
  useRef,
  useContext,
  useMemo,
  useCallback,
} from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  IconButton,
  Button,
  Alert,
  AlertTitle,
  Theme,
} from "@mui/material";
import debounce from "lodash/debounce";
import {
  ArrowLeft,
  Ellipsis,
  EllipsisVertical,
  FileWarning,
  Globe,
  Lock,
  MessagesSquare,
} from "lucide-react";
import {
  Document,
  Permission,
  DocumentType,
  DocumentScope,
  DocumentStatus,
} from "protogen/documents_pb";
import {
  UpdateDocumentRequest,
  CopyDocumentRequest,
  CopyMemberDocumentRequest,
} from "protogen/documents_service_pb";
import { ContentUpdates } from "protogen/notes_pb";
import {
  useCopyDocument,
  useCreateEmailDraftFromDocument,
  useGetDocument,
  useGetPublicDocument,
  useUpdateDocument,
  useCopyMemberDocument,
} from "services/documents";
import SplitPanel from "components/details-page/SplitPanel";
import GridPage from "components/layout/GridPage";
import { stripImgSrc, toMaybeNumber } from "common/utils";
import DocumentViewer from "components/editor/DocumentViewer";
import DocumentEditor from "components/editor/DocumentEditor";
import useAutoSave from "components/editor/useAutoSave";
import Loading from "components/common/Loading";
import useIsMobile from "components/hooks/useIsMobile";
import Breadcrumbs from "components/common/Breadcrumbs";
import LinkRouter from "components/navigation/LinkRouter";
import { Attachment } from "protogen/common_pb";
import { Handle } from "components/editor/DocumentEditor";
import DropdownMenu from "components/common/DropdownMenu";
import {
  getDocumentLink,
  getScopeLabel,
  isListDocument,
  isManagedBySmsBot,
} from "components/documents/utils";
import { CurrentUserContext } from "components/context/RequireAuth";
import { useSnackpack } from "components/context/SnackpackContextProvider";
import { useListAdvisorFamilies } from "services/advisor";
import {
  createMenuOption,
  SubmenuMenuOption,
} from "components/common/PopperMenu";
import BookmarkToTaskDialog, {
  fromDocument,
} from "components/tasks/BookmarkToTaskDialog";
import { Family } from "protogen/advisors_service_pb";
import ConfirmationDialog, {
  useConfirmationDialog,
} from "components/common/ConfirmationDialog";
import { TaskBlockStatus } from "protogen/tasks_pb";
import {
  useCreateEmailDraftFromDocumentBlock,
  useUpdateTaskBlockStatusRequest,
} from "services/tasks";
import CheckedDropDown from "components/common/CheckedDropDown";
import AddPostDialog, {
  Props as AddPostDialogProps,
} from "components/forum/AddPostDialog";
import { ForumPostType } from "protogen/forum_service_pb";
import useTitle from "components/hooks/useTitle";
import CopyToButton from "components/documents/CopyToButton";
import EmailComposeDialog from "components/email/EmailComposeDialog";
import { EmailMessage } from "protogen/conversation_pb";
import {
  usePictureInPictureContentContext,
  PictureInPictureContentProvider,
} from "components/context/PictureInPictureContext";
import { isPictureInPictureAvailable } from "common/picture-in-picture/PictureInPicture";
import { DocumentFavoriteIcon } from "components/documents/DocumentFavoriteIcon";
import DeletedDocumentAlert from "components/documents/DeletedDocumentAlert";
import FlexBox from "components/common/FlexBox";
import TextField from "components/common/TextField";
import { isUserAdmin } from "common/userUtils";
import DocumentLabels from "components/documents/DocumentLabels";
import AuthService from "services/auth";
import EditLockAlert from "./EditLockAlert";
import VersionErrorAlert from "./VersionErrorAlert";
import MiniFeed from "components/forum/MiniFeed";
import { ActivityEntityType } from "protogen/common_pb";
import { PROD_MEMBER_DOMAIN } from "components/context/constants";
import ListDocumentView, {
  formatHtmlContent,
  formatTextContent,
  parseListContent,
} from "./ListDocumentView";

const DOCUMENTS_STATIC_CHOICE = "Family documents";

/**
 * Gets the appropriate hash from a document based on its type
 */
const getDocumentHash = (document: Document | undefined): string => {
  if (!document) return "";

  if (
    document.documentType === DocumentType.NOTE ||
    document.documentType === DocumentType.LIST
  ) {
    return document.note?.hash || "";
  }

  // TaskBlock documents don't have a hash field, return empty string
  return "";
};

function LoginRequiredAlert({
  isMemberDocument,
}: {
  isMemberDocument?: boolean;
}) {
  const navigate = useNavigate();
  return (
    <Alert
      severity="warning"
      icon={false}
      sx={{
        borderRadius: "10px",
        px: 3,
        py: 2,
        "& .MuiAlert-message": { width: "100%" },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "center" },
          gap: { xs: 1.5, sm: 3 },
          width: "100%",
        }}
      >
        <Box sx={{ flex: 1 }}>
          <AlertTitle
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              "& svg": { color: "warning.main" },
            }}
          >
            <Lock size={18} />
            Sign in to access this document
          </AlertTitle>
          You'll need to sign in before you can access this document.
        </Box>
        <Button
          variant="contained"
          size="small"
          sx={{
            flexShrink: 0,
            width: { xs: "100%", sm: "auto" },
          }}
          onClick={() => {
            const destination = encodeURIComponent(
              window.location.pathname + window.location.search,
            );
            const loginPath = isMemberDocument ? "/member-login" : "/login";
            navigate(`${loginPath}?destination=${destination}`);
          }}
        >
          Sign in
        </Button>
      </Box>
    </Alert>
  );
}

interface DocumentPageProps {
  /** Whether this is for member documents (uses public/auth-based fetching) */
  isMemberDocument?: boolean;
  /** Whether to show full action menus and navigation */
  showFullActions?: boolean;
}

interface InitialContent {
  content: string;
  attachments: Attachment[];
  contentType?: string;
}

const postPropsFromDocument = (document: Document) => {
  const title = document?.title || "";
  const link = `${window.location.origin}/documents/${document?.uuid}`;
  const shareContent = {
    type: "doc",
    content: [
      {
        type: "paragraph",
        content: [
          {
            type: "text",
            text: "Checkout my document ",
          },
          {
            type: "text",
            marks: [
              {
                type: "italic",
              },
            ],
            text: title,
          },
        ],
      },
      {
        type: "paragraph",
        content: [
          {
            type: "thumbnailURL",
            attrs: {
              url: link,
              displayMode: 1,
              title: title,
              header: "Document",
              description: `By ${document?.owner?.displayName}`,
              image: "/assets/images/document-placeholder.png",
              loaded: true,
            },
          },
        ],
      },
    ],
  };
  const textContent = `Checkout my document ${title}\\n${link}`;
  return {
    initialSubject: document?.title || undefined,
    initialContent: {
      payload: JSON.stringify(shareContent),
      textContent: textContent,
    },
    postType: ForumPostType.POST_DISCUSSION,
    topics: ["Documents"],
  };
};

const _Wrapped = ({
  document,
  initialContent,
  error,
  contentRef,
  editable,
  isMemberDocument,
  pipButton,
  getCurrentHash,
  onDocumentUpdate,
  labelContent,
}: {
  document: Document | undefined;
  initialContent: InitialContent | undefined;
  error: string | null;
  contentRef: React.RefObject<HTMLDivElement>;
  editable: boolean;
  isMemberDocument?: boolean;
  pipButton?: React.ReactNode;
  getCurrentHash: () => string;
  onDocumentUpdate: (updatedDocument: Document) => void;
  labelContent?: React.ReactNode;
}) => {
  const [title, setTitle] = useState(document?.title);
  useTitle(title || "Untitled Document");
  const inputRef = useRef<HTMLInputElement>(null);
  const [searchParams] = useSearchParams();
  const editorRef = useRef<Handle>(null);
  const isNew = searchParams.get("created") === "1";
  const isList = document?.documentType === DocumentType.LIST;
  const documentInitialContent =
    initialContent?.contentType === "html"
      ? initialContent.content
      : isList
        ? initialContent?.content || "{}"
        : JSON.parse(initialContent?.content || "{}");

  const { request, error: updateError } = useUpdateDocument((r) => {
    onDocumentUpdate(r.document);
  });

  const saveTitle = useMemo(
    () =>
      debounce(
        async (title: string) => {
          await request(
            new UpdateDocumentRequest({
              documentRef: document?.ref || "",
              title: title,
              shouldUpdateTitle: true,
              prevHash: getCurrentHash(),
            }),
          );
        },
        1500,
        { trailing: true },
      ),
    [document?.ref],
  );

  useEffect(() => {
    return () => {
      saveTitle.cancel();
    };
  }, [saveTitle]);

  useEffect(() => {
    setTitle(document?.title);
  }, [document?.title]);

  // Get PiP context from global storage if available (only for regular documents)
  const pipContext =
    !isMemberDocument && typeof window !== "undefined"
      ? (window as any).__pipContext
      : null;

  const { setEditorContent, autoSaveStatus } = useAutoSave(
    initialContent?.content || null,
    toMaybeNumber(Number(document?.lastUpdatedSec)),
    async (c) => {
      await request(
        new UpdateDocumentRequest({
          documentRef: document?.ref || "",
          content: new ContentUpdates({
            contentType: "html",
            payload: stripImgSrc(c.html),
            textContent: c.text,
            attachments: c.attachments,
          }),
          shouldUpdateContent: true,
          prevHash: getCurrentHash(),
        }),
      );
    },
    !!error,
    1500,
    initialContent?.contentType,
  );

  // Set up Picture-in-Picture content when context is available
  useEffect(() => {
    if (pipContext?.setPipContentProps && document && setEditorContent) {
      pipContext.setPipContentProps({
        pictureName: "document",
        setContent: setEditorContent,
        passiveEditor: false,
        placeholder: "Write something...",
        initialContent: documentInitialContent,
        attachmentsEnabled: true,
        initialAttachments: initialContent?.attachments || [],
        editorDefaultFontSize: "15px",
        placement: "top",
        scrollable: true,
        editorRef: editorRef,
        linkUnfurlEnabled: true,
        autoSaveStatus: autoSaveStatus,
        editorMinHeight: "calc(100vh - 119px)",
        editorMaxHeight: "calc(100vh - 119px)",
        documentTitle: document?.title || "",
      });
    }
  }, [pipContext, document, documentInitialContent, initialContent, editorRef]);

  // Clean up PiP content when component unmounts
  useEffect(() => {
    return () => {
      if (pipContext?.setPipContentProps) {
        pipContext.setPipContentProps(null);
      }
    };
  }, [pipContext]);

  useEffect(() => {
    if (inputRef.current && isNew) {
      const length = inputRef.current.value.length;
      inputRef.current.focus();
      inputRef.current.setSelectionRange(length, length);
    }
  }, []);

  const isMobile = useIsMobile();
  const isVersionError =
    updateError &&
    typeof updateError === "string" &&
    updateError.includes("NoteVersionError");

  if (isList && document) {
    return (
      <Box sx={{ marginTop: "8px", position: "relative" }}>
        {editable && <EditLockAlert show={!!document?.editLock} />}
        <ListDocumentView
          document={document}
          title={title || ""}
          content={initialContent?.content}
          editable={editable}
          initialAttachments={initialContent?.attachments}
          onTitleChange={(nextTitle) => {
            setTitle(nextTitle);
            saveTitle(nextTitle);
          }}
          onContentChange={async (payload, textContent, attachments) => {
            await request(
              new UpdateDocumentRequest({
                documentRef: document?.ref || "",
                content: new ContentUpdates({
                  contentType: "json",
                  payload,
                  textContent,
                  attachments,
                }),
                shouldUpdateContent: true,
                prevHash: getCurrentHash(),
              }),
            );
          }}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ marginTop: "8px", position: "relative" }}>
      <Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {editable ? (
            <TextField
              inputRef={inputRef}
              variant="standard"
              fullWidth
              placeholder="New document"
              value={title}
              autoFocus={isNew}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const title = e.target.value;
                setTitle(title);
                saveTitle(title);
              }}
              margin="none"
              InputProps={{
                disableUnderline: true,
                sx: (theme: Theme) => ({
                  ...theme.typography.h3,
                }),
              }}
            />
          ) : (
            <Typography
              variant="h3"
              color="text.primary"
              sx={{ marginBottom: isMobile ? "8px" : "12px" }}
            >
              {title}
            </Typography>
          )}
          {!isMobile && labelContent}
        </Box>

        {/* Version Error Alert */}
        {editable && (
          <VersionErrorAlert show={!!isVersionError} editorRef={editorRef} />
        )}

        {/* Edit Lock Alert */}
        {editable && <EditLockAlert show={!!document?.editLock} />}

        {editable ? (
          <Box ref={contentRef}>
            <DocumentEditor
              setContent={setEditorContent}
              passiveEditor={false}
              placeholder={"Write something..."}
              initialContent={documentInitialContent}
              attachmentsEnabled={true}
              initialAttachments={initialContent?.attachments}
              editorDefaultFontSize={"15px"}
              placement={"top"}
              scrollable={true}
              ref={editorRef}
              editorMinHeight={
                isMobile ? "calc(100vh - 290px)" : "calc(100vh - 220px)"
              }
              linkUnfurlEnabled={true}
              editorMaxHeight={
                isMobile ? "calc(100vh - 290px)" : "calc(100vh - 220px)"
              }
              autoSaveStatus={autoSaveStatus}
              rightActions={
                pipButton
                  ? [pipButton]
                  : pipContext?.isPipAvailable && pipContext?.PipButton
                    ? [
                        React.createElement(pipContext.PipButton, {
                          key: "pip-button",
                        }),
                      ]
                    : []
              }
              sx={{ paddingBottom: "20px" }}
            />
          </Box>
        ) : (
          <Box>
            {!isMemberDocument ? (
              <DocumentViewer
                content={documentInitialContent}
                attachments={initialContent?.attachments}
                minHeight="29px"
                sx={{ padding: "0", marginBottom: "16px" }}
              />
            ) : (
              <>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: isMobile ? "8px" : "12px",
                  }}
                >
                  {document && (
                    <DocumentLabels
                      initialDocument={document}
                      editable={false}
                      clickable={false}
                    />
                  )}
                </Box>
                <DocumentViewer
                  content={documentInitialContent}
                  attachments={initialContent?.attachments}
                  minHeight="29px"
                  sx={{ padding: "0", marginBottom: "16px" }}
                />
              </>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

const DocumentBreadcrumbs = ({
  document,
  isTaskblock,
}: {
  document: Document;
  isTaskblock: boolean;
}) => {
  if (!isTaskblock) return null;
  return (
    <FlexBox gap="8px" alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
      <LinkRouter
        to={`/tasks/${encodeURIComponent(document?.ref)}`}
        sx={{
          justifyContent: "start",
          minWidth: 0,
          flex: 1,
        }}
      >
        <Typography
          variant="bodyHeavy"
          color="primary"
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            fontWeight: 600,
            minWidth: 0,
          }}
        >
          {document?.subtitle}
        </Typography>
      </LinkRouter>
    </FlexBox>
  );
};

export default function DocumentPage({
  isMemberDocument = false,
  showFullActions = true,
}: DocumentPageProps) {
  const currentUser = AuthService.getCurrentUser();
  const contextUser = useContext(CurrentUserContext);
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const docType = searchParams.get("doc_type");
  const showComments = searchParams.has("comments");
  const isMobile = useIsMobile();
  const [document, setDocument] = useState<Document>();
  const { pushSnack } = useSnackpack();
  const [initialContent, setInitialContent] = useState<InitialContent>();
  const [shareDialogOpen, setShareDialogOpen] =
    useState<Partial<AddPostDialogProps> | null>(null);
  const [copyPopperOpen, setCopyPopperOpen] = useState(false);
  const [copyToOptions, setCopyToOptions] = useState<any[]>([]);
  const [copyToFamily, setCopyToFamily] = useState<Family | undefined>(
    undefined,
  );
  const { request: familiesRequest } = useListAdvisorFamilies();
  const { request: updateBlockStatus } = useUpdateTaskBlockStatusRequest();
  const confirmState = useConfirmationDialog();
  const { request: createDraftRequest } = useCreateEmailDraftFromDocument();
  const { request: createDraftRequestFromTaskBlock } =
    useCreateEmailDraftFromDocumentBlock();
  const [emailComposeOpen, setEmailComposeOpen] = useState(false);
  const [emailDraft, setEmailDraft] = useState<EmailMessage | undefined>(
    undefined,
  );
  const [commentCount, setCommentCount] = useState(0);
  const contentRef = useRef<HTMLDivElement>(null);

  // Keep track of the current hash to ensure we always use the most recent one
  const currentHashRef = useRef<string>("");

  // Update hash ref whenever document changes
  useEffect(() => {
    currentHashRef.current = getDocumentHash(document);
  }, [document]);

  // Function to get the current hash
  const getCurrentHash = useCallback(() => currentHashRef.current, []);

  // Callback to update document and hash when document is updated
  const onDocumentUpdate = useCallback(
    (updatedDocument: Document) => {
      currentHashRef.current = getDocumentHash(updatedDocument);
    },
    [document?.family],
  );

  // Picture-in-Picture functionality is available for regular documents when wrapped
  // in the PictureInPictureContentProvider. Member documents skip PiP to avoid
  // context dependency issues.

  // Choose the appropriate document fetching function
  const getDocumentFn = isMemberDocument
    ? currentUser
      ? useGetDocument
      : useGetPublicDocument
    : useGetDocument;

  const { request, loading, error, errorCode } = getDocumentFn((r) => {
    if (Number(docType) === DocumentType.TASK_BLOCK) {
      setInitialContent({
        content: r?.document?.taskBlock?.content || "",
        attachments: r?.document?.taskBlock?.attachments || [],
        contentType: r?.document?.taskBlock?.contentType,
      });
    } else {
      setInitialContent({
        content: r?.document?.note?.content?.payload || "",
        attachments: r?.document?.note?.content?.attachments || [],
        contentType: r?.document?.note?.content?.contentType,
      });
    }
    setDocument(r.document);

    // URL rewriting for regular documents
    if (
      !isMemberDocument &&
      r.document?.documentType === DocumentType.NOTE &&
      params.documentRef === r.document.ref &&
      r.document.uuid
    ) {
      const searchParams = new URLSearchParams(window.location.search);
      navigate(`/documents/${r.document.uuid}?${searchParams.toString()}`, {
        replace: true,
      });
    }
  });

  // Choose appropriate copy function
  const { request: regularCopyRequest } = useCopyDocument();
  const { request: memberCopyRequest } = useCopyMemberDocument();

  const { request: updateDocumentRequest } = useUpdateDocument((r) => {
    setDocument({ ...r.document, family: document?.family });
    // Update the current hash ref with the new hash from the response
    currentHashRef.current = getDocumentHash(r.document);
  });

  const breadcrumbs = document?.family
    ? [
        {
          name: document.family.name,
          link: isMemberDocument
            ? "/home"
            : `/families/${encodeURIComponent(document.family.ref)}`,
        },
        { name: "Documents", back: true },
      ]
    : [{ name: "Documents", back: true }];
  const isTaskBlock = Number(docType) === DocumentType.TASK_BLOCK;
  const isList = !!document && isListDocument(document);
  const canEdit = document?.permission === Permission.WRITE && !isTaskBlock;
  const isAdmin = isUserAdmin(contextUser);
  const [viewerEditMode, setViewerEditMode] = useState(false);
  const isEditing = canEdit || viewerEditMode;

  const refreshDocument = useCallback(() => {
    if (!params.documentRef) return;
    request({
      documentRef: params.documentRef,
      documentType: docType,
      isFormDefinition: false,
      markAsRead: !!currentUser,
    });
  }, [params.documentRef, docType, request]);

  useEffect(() => {
    refreshDocument();
  }, [params.documentRef]);

  // Member document copy handler
  const onCopyMemberDocument = async () => {
    if (!document || !contextUser) return;
    const result = await memberCopyRequest(
      new CopyMemberDocumentRequest({
        fromDocumentType: document.documentType,
        fromEntityType: document.inboxType === "family" ? "family" : "advisor",
        fromEntityRef: document.ref,
        creationSourceName: "note",
        title: document.title ? "Copy of " + document.title : "",
        replaceTitle: true,
      }),
    );
    if (result) {
      pushSnack({
        message: "Document copied to your documents.",
        alertSeverity: "success",
        key: `document-copied-${document.ref}`,
      });

      navigate(
        `/documents/${encodeURIComponent(
          result?.document?.uuid || "",
        )}?created=1`,
      );
    }
  };

  // Regular document copy handlers (from original Document.tsx)

  const onScopeChange = (scope: DocumentScope) => {
    updateDocumentRequest(
      new UpdateDocumentRequest({
        documentRef: document?.ref || "",
        scope: scope,
        prevHash: getCurrentHash(),
      }),
    );
  };

  const copyDocument = async (
    document: Document,
    toDocumentType: DocumentType,
    toEntityType: string,
    toEntityRef: string,
  ) => {
    const result = await regularCopyRequest(
      new CopyDocumentRequest({
        fromDocumentType: document?.documentType,
        fromEntityType:
          document?.documentType === DocumentType.TASK_BLOCK
            ? "task-block"
            : document?.documentType === DocumentType.NOTE &&
                document?.inboxType === "family"
              ? "family"
              : "advisor",
        fromEntityRef:
          document?.documentType === DocumentType.TASK_BLOCK
            ? document?.taskBlock?.ref
            : document?.ref,
        toDocumentType: toDocumentType,
        toEntityType: toEntityType,
        toEntityRef: toEntityRef,
        creationSourceName:
          toDocumentType === DocumentType.NOTE ? "note" : "task-block",
        title: document?.title ? "Copy of " + document.title : "",
        replaceTitle: true,
      }),
    );
    return result;
  };

  const onCopyDocumentHandler = async (doc: Document | undefined) => {
    if (isMemberDocument) {
      return onCopyMemberDocument();
    }

    const result = await copyDocument(
      doc!,
      DocumentType.NOTE,
      "advisor",
      contextUser.ref,
    );
    setCopyPopperOpen(false);
    navigate(
      `/documents/${encodeURIComponent(
        result?.document?.uuid || "",
      )}?created=1`,
    );
  };

  const onLoad = async () => {
    if (isMemberDocument || !showFullActions) return;

    const familiesData = await familiesRequest();
    const loadedFamilies = familiesData?.families || [];
    if (loadedFamilies.length > 0) {
      setCopyToOptions([
        createMenuOption(
          "My documents",
          () => {
            if (document) {
              onCopyDocumentHandler(document);
            }
          },
          "",
        ),
        ...(document?.documentType === DocumentType.NOTE &&
        document?.inboxType === "family"
          ? [
              createMenuOption(
                `${document?.family?.name} task`,
                () => {
                  const family = loadedFamilies.find(
                    (f) => f.ref === document?.family?.ref,
                  );
                  setCopyToFamily(family);
                },
                "",
              ),
            ]
          : []),
        <SubmenuMenuOption
          key="families"
          title="Families"
          onClick={(value) => {
            setCopyPopperOpen(false);
            const family = loadedFamilies.find((f) => f.ref === value);
            setCopyToFamily(family);
          }}
          submenuOptions={loadedFamilies.map((f) => ({
            label: f.name,
            value: f.ref,
          }))}
        />,
      ]);
    }
  };

  useEffect(() => {
    if (document) {
      onLoad();
    }
  }, [document]);

  const onBookmarkHandler = async (tasRefs: string[]) => {
    if (tasRefs.length < 1) return;
    const taskRef = tasRefs[0];
    const documentType =
      taskRef === DOCUMENTS_STATIC_CHOICE
        ? DocumentType.NOTE
        : DocumentType.TASK_BLOCK;
    const entityType =
      taskRef === DOCUMENTS_STATIC_CHOICE ? "family" : "task-block";
    const entityRef =
      taskRef === DOCUMENTS_STATIC_CHOICE ? copyToFamily?.ref || "" : taskRef;
    const result = await copyDocument(
      document!,
      documentType,
      entityType,
      entityRef,
    );
    setCopyToFamily(undefined);
    if (documentType === DocumentType.NOTE) {
      navigate(
        `/documents/${encodeURIComponent(
          result?.document?.uuid || "",
        )}?created=1`,
      );
    } else {
      navigate(
        `/tasks/${encodeURIComponent(result?.document?.ref || "")}?created=1`,
      );
    }
  };

  const onDeleteDocument = async () => {
    if (!document) return;
    if (
      document.documentType === DocumentType.NOTE ||
      document.documentType === DocumentType.LIST
    ) {
      confirmState.openDialog(async () => {
        await updateDocumentRequest(
          new UpdateDocumentRequest({
            documentRef: document.ref,
            status: DocumentStatus.DELETED,
            prevHash: isMemberDocument ? "" : getCurrentHash(),
          }),
        );
        navigate(-1);
      });
    } else if (document.documentType === DocumentType.TASK_BLOCK) {
      confirmState.openDialog(async () => {
        await updateBlockStatus({
          taskBlockRef: document.taskBlock?.ref,
          status: TaskBlockStatus.DELETED,
        });
        navigate(-1);
      });
    }
  };

  const onPrintDocument = () => {
    const printWindow = window.open("", "_blank");
    const content =
      document?.documentType === DocumentType.NOTE
        ? document?.note?.content?.payload
        : document?.taskBlock?.content;
    const title = document?.title || "";
    const attachments =
      document?.documentType === DocumentType.NOTE
        ? document?.note?.content?.attachments
        : document?.taskBlock?.attachments;

    if (printWindow) {
      printWindow.document.write(content || "");
      printWindow.document.close();
      printWindow.focus();
      printWindow.document.title = title || "";
      setTimeout(
        () => {
          printWindow.print();
          printWindow.close();
        },
        1000 + attachments.length * 500,
      );
    }
  };

  const copyLinkHandler = () => {
    if (!document) return;
    pushSnack({
      message: "Document link copied.",
      alertSeverity: "success",
      key: `document-${document?.ref}`,
    });
    navigator.clipboard.writeText(
      `${window.location.origin}${getDocumentLink(document)}`,
    );
  };

  const copyMemberLinkHandler = () => {
    if (!document?.uuid) return;
    pushSnack({
      message: "Member link copied.",
      alertSeverity: "success",
      key: `document-member-link-${document?.ref}`,
    });
    navigator.clipboard.writeText(
      `${PROD_MEMBER_DOMAIN}/documents/${document?.uuid}`,
    );
  };

  const isMemberLinkEnabled =
    !!document?.family?.ref &&
    (document?.scope === DocumentScope.FAMILY ||
      document?.scope === DocumentScope.PUBLIC);

  const onCreateEmail = async () => {
    if (!document) return;
    let email: EmailMessage | undefined;
    if (document.documentType === DocumentType.TASK_BLOCK) {
      const response = await createDraftRequestFromTaskBlock({
        taskBlockRef: document.taskBlock?.ref,
      });
      email = response?.email;
      setEmailComposeOpen(true);
    } else {
      const response = await createDraftRequest({
        documentRef: document.ref,
        familyRef: document.family?.ref || "",
      });
      email = response?.email;
    }
    if (!email) return;
    setEmailDraft(email);
    setEmailComposeOpen(true);
  };

  const documentActionButtons = (
    document: Document,
    dropdownOptions: {
      title: string | React.ReactNode;
      onClick: () => void;
      disabled?: boolean;
    }[],
  ) => {
    if (isMemberDocument) {
      return (
        <Box sx={{ display: "flex", gap: "8px", alignItems: "center" }}>
          {document.inboxType === "family" &&
            document.permission === Permission.WRITE &&
            !isSmsBot && (
              <CheckedDropDown
                key={document.scope}
                radio={true}
                disabled={false}
                displayValue={getScopeLabel(document.scope) || ""}
                hasIcon={false}
                sx={{
                  color: "#6B6E7B",
                  "& .MuiOutlinedInput-root": { paddingRight: "0" },
                }}
                sxSelect={{
                  fieldset: { border: "none !important" },
                  "& .MuiOutlinedInput-notchedOutline": { paddingRight: "0px" },
                  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
                    {
                      border: "none !important",
                    },
                  ".MuiSelect-select.MuiSelect-outlined.MuiSelect-multiple": {
                    paddingRight: "0px",
                    paddingLeft: "0px",
                    marginTop: "0px",
                    span: { color: "#6B6E7B", fontWeight: 600 },
                  },
                }}
                options={[
                  {
                    label: "Private",
                    value: DocumentScope.MEMBER_PRIVATE.toString(),
                    defaultChecked:
                      document?.scope === DocumentScope.MEMBER_PRIVATE,
                    mutuallyExclusive: true,
                  },
                  {
                    label: "Family",
                    value: DocumentScope.FAMILY.toString(),
                    defaultChecked: document?.scope === DocumentScope.FAMILY,
                    mutuallyExclusive: true,
                  },
                ]}
                onChange={(r) => {
                  const value = parseInt(r[0].value);
                  onScopeChange(value as DocumentScope);
                }}
              />
            )}
          <DropdownMenu
            disabled={false}
            fullWidth
            buttonContent={
              isMobile ? (
                <EllipsisVertical size={20} stroke="#6B6E7B" />
              ) : (
                <Ellipsis size={20} stroke="#6B6E7B" />
              )
            }
            dropdownIcon={false}
            variant="text"
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            options={
              currentUser
                ? [
                    ...dropdownOptions,
                    {
                      title: "Duplicate",
                      onClick: onCopyMemberDocument,
                    },
                  ]
                : dropdownOptions
            }
            sx={{ minWidth: "auto" }}
          />
        </Box>
      );
    }

    if (isListDocument(document)) {
      return (
        <Box
          sx={{
            display: "flex",
            gap: "12px",
            width: isMobile ? "100%" : "auto",
            alignItems: "center",
          }}
        >
          <DocumentFavoriteIcon
            document={document!}
            showCount={false}
            showTooltip={false}
            sx={{ color: "#6B6E7B" }}
          />
          {!!document.scope &&
            document.permission === Permission.WRITE &&
            !isSmsBot && (
              <CheckedDropDown
                key={document.scope}
                radio={true}
                disabled={false}
                displayValue={getScopeLabel(document.scope)}
                hasIcon={false}
                sx={{
                  color: "#6B6E7B",
                  "& .MuiOutlinedInput-root": { paddingRight: "0" },
                }}
                sxSelect={{
                  fieldset: {
                    border: "none !important",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    paddingRight: "0px",
                  },
                  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
                    {
                      border: "none !important",
                    },
                  ".MuiSelect-select.MuiSelect-outlined.MuiSelect-multiple": {
                    paddingRight: "0px",
                    paddingLeft: "0px",
                    marginTop: "0px",
                    span: {
                      color: "#6B6E7B",
                      fontWeight: 600,
                    },
                  },
                }}
                options={[
                  {
                    label: "Private",
                    value: DocumentScope.PRIVATE.toString(),
                    defaultChecked: document?.scope === DocumentScope.PRIVATE,
                    mutuallyExclusive: true,
                  },
                  {
                    label: "Advisors with link",
                    value: DocumentScope.INTERNAL.toString(),
                    defaultChecked: document?.scope === DocumentScope.INTERNAL,
                    mutuallyExclusive: true,
                  },
                  {
                    label: "Any Advisor",
                    value: DocumentScope.INTERNAL_DISCOVERABLE.toString(),
                    defaultChecked:
                      document?.scope === DocumentScope.INTERNAL_DISCOVERABLE,
                    mutuallyExclusive: true,
                  },
                  ...(document?.family
                    ? [
                        {
                          label: "Family",
                          value: DocumentScope.FAMILY.toString(),
                          defaultChecked:
                            document?.scope === DocumentScope.FAMILY,
                          mutuallyExclusive: true,
                        },
                      ]
                    : []),
                  {
                    label: "Public with the link",
                    value: DocumentScope.PUBLIC.toString(),
                    defaultChecked: document?.scope === DocumentScope.PUBLIC,
                    mutuallyExclusive: true,
                    endIcon: (
                      <Globe
                        size={16}
                        color="#8E9598"
                        style={{ marginLeft: "6px" }}
                      />
                    ),
                  },
                ]}
                onChange={(r) => {
                  const value = parseInt(r[0].value);
                  onScopeChange(value as DocumentScope);
                }}
              />
            )}
          {!!document.scope && document.permission === Permission.READ && (
            <Typography
              variant="bodyHeavy"
              color="text.tertiary"
              sx={{
                whiteSpace: "nowrap",
                alignSelf: "center",
                userSelect: "none",
              }}
            >
              {!isAdmin ? "View only" : "View and edit"}
            </Typography>
          )}
          <DropdownMenu
            disabled={false}
            fullWidth
            buttonContent={<Ellipsis size={20} stroke="#6B6E7B" />}
            dropdownIcon={false}
            variant="text"
            options={dropdownOptions}
            sx={{ minWidth: "auto" }}
          />
        </Box>
      );
    }

    return (
      <Box
        sx={{
          display: "flex",
          gap: "12px",
          width: isMobile ? "100%" : "auto",
          alignItems: "center",
        }}
      >
        <DocumentFavoriteIcon
          document={document!}
          showCount={false}
          showTooltip={false}
          sx={{ color: "#6B6E7B" }}
        />

        {/* Visibility scope */}
        {!!document.scope &&
          document.permission === Permission.WRITE &&
          !isSmsBot && (
            <CheckedDropDown
              key={document.scope}
              radio={true}
              disabled={false}
              displayValue={getScopeLabel(document.scope)}
              hasIcon={false}
              sx={{
                color: "#6B6E7B",
                "& .MuiOutlinedInput-root": { paddingRight: "0" },
              }}
              sxSelect={{
                fieldset: {
                  border: "none !important",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  paddingRight: "0px",
                },
                "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
                  {
                    border: "none !important",
                  },
                ".MuiSelect-select.MuiSelect-outlined.MuiSelect-multiple": {
                  paddingRight: "0px",
                  paddingLeft: "0px",
                  marginTop: "0px",
                  span: {
                    color: "#6B6E7B",
                    fontWeight: 600,
                  },
                },
              }}
              options={[
                {
                  label: "Private",
                  value: DocumentScope.PRIVATE.toString(),
                  defaultChecked: document?.scope === DocumentScope.PRIVATE,
                  mutuallyExclusive: true,
                },
                {
                  label: "Advisors with link",
                  value: DocumentScope.INTERNAL.toString(),
                  defaultChecked: document?.scope === DocumentScope.INTERNAL,
                  mutuallyExclusive: true,
                },
                {
                  label: "Any Advisor",
                  value: DocumentScope.INTERNAL_DISCOVERABLE.toString(),
                  defaultChecked:
                    document?.scope === DocumentScope.INTERNAL_DISCOVERABLE,
                  mutuallyExclusive: true,
                },
                ...(document?.family
                  ? [
                      {
                        label: "Family",
                        value: DocumentScope.FAMILY.toString(),
                        defaultChecked:
                          document?.scope === DocumentScope.FAMILY,
                        mutuallyExclusive: true,
                      },
                    ]
                  : []),
                {
                  label: "Public with the link",
                  value: DocumentScope.PUBLIC.toString(),
                  defaultChecked: document?.scope === DocumentScope.PUBLIC,
                  mutuallyExclusive: true,
                  endIcon: (
                    <Globe
                      size={16}
                      color="#8E9598"
                      style={{ marginLeft: "6px" }}
                    />
                  ),
                },
              ]}
              onChange={(r) => {
                const value = parseInt(r[0].value);
                onScopeChange(value as DocumentScope);
              }}
            />
          )}
        {!!document.scope && document.permission === Permission.READ && (
          <Typography
            variant="bodyHeavy"
            color="text.tertiary"
            sx={{
              whiteSpace: "nowrap",
              alignSelf: "center",
              userSelect: "none",
            }}
          >
            {!isAdmin ? "View only" : "View and edit"}
          </Typography>
        )}

        <CopyToButton
          isOpen={copyPopperOpen}
          menuOptions={copyToOptions}
          setIsOpen={setCopyPopperOpen}
        />
        <DropdownMenu
          disabled={false}
          fullWidth
          buttonContent={<Ellipsis size={20} stroke="#6B6E7B" />}
          dropdownIcon={false}
          variant="text"
          options={dropdownOptions}
          sx={{ minWidth: "auto" }}
        />
      </Box>
    );
  };

  const isSmsBot = document ? isManagedBySmsBot(document) : false;
  const additionalOptions: {
    title: string | React.ReactNode;
    onClick: () => void;
    disabled?: boolean;
  }[] = isList
    ? [
        {
          title: "Print",
          onClick: () => {
            if (!document) return;
            const listContent = parseListContent(
              document.note?.content?.payload,
            );
            if (!listContent) return;
            const html = formatHtmlContent(
              document.title || "Untitled",
              listContent,
              document.note?.content?.attachments,
            );
            const printWindow = window.open("", "_blank");
            if (printWindow) {
              printWindow.document.write(html);
              printWindow.document.close();
              printWindow.focus();
              printWindow.document.title = document.title || "Untitled";
              setTimeout(() => {
                printWindow.print();
                printWindow.close();
              }, 500);
            }
          },
        },
        {
          title: "Copy content",
          onClick: () => {
            if (!document) return;
            const listContent = parseListContent(
              document.note?.content?.payload,
            );
            if (!listContent) return;
            const html = formatHtmlContent(
              document.title || "Untitled",
              listContent,
              document.note?.content?.attachments,
            );
            const text = `${
              document.title || "Untitled"
            }\n\n${formatTextContent(listContent)}`;
            navigator.clipboard.write([
              new ClipboardItem({
                "text/html": new Blob([html], { type: "text/html" }),
                "text/plain": new Blob([text], { type: "text/plain" }),
              }),
            ]);
            pushSnack({
              message: "List content copied.",
              alertSeverity: "success",
              key: `document-copy-${document.ref}`,
            });
          },
        },
        ...(!isMemberDocument
          ? [
              {
                title: "Send as email",
                onClick: () => onCreateEmail(),
              },
            ]
          : []),
        ...(!isMemberDocument && !isSmsBot
          ? [
              {
                title: "Delete",
                onClick: () => onDeleteDocument(),
              },
            ]
          : []),
      ]
    : [
        ...(isMemberDocument
          ? []
          : [
              {
                title: "Share to Community",
                onClick: () => {
                  if (!document) return;
                  setShareDialogOpen(postPropsFromDocument(document));
                },
                disabled: !!document?.family?.ref,
              },
              {
                title: "Send as email",
                onClick: () => onCreateEmail(),
              },
            ]),
        {
          title: "Print",
          onClick: () => onPrintDocument(),
        },
        ...(!canEdit &&
        isAdmin &&
        document?.documentType === DocumentType.NOTE &&
        !isMemberDocument
          ? [
              {
                title: isEditing ? "Cancel editing" : "Edit document",
                onClick: () => setViewerEditMode(!isEditing),
              },
            ]
          : []),
        ...(!isMemberDocument && !isSmsBot
          ? [
              {
                title: "Delete",
                onClick: () => onDeleteDocument(),
              },
            ]
          : isMemberDocument &&
              document?.owner?.ref === contextUser.ref &&
              !isSmsBot
            ? [
                {
                  title: "Delete",
                  onClick: () => onDeleteDocument(),
                },
              ]
            : []),
      ];

  if (
    !isList &&
    !isTaskBlock &&
    !isMemberDocument &&
    (document?.scope === DocumentScope.INTERNAL ||
      document?.scope === DocumentScope.INTERNAL_DISCOVERABLE ||
      document?.scope === DocumentScope.FAMILY ||
      document?.scope === DocumentScope.PUBLIC)
  ) {
    additionalOptions.unshift({
      title: "Copy link",
      onClick: () => copyLinkHandler(),
    });
  }

  if (!isList && !isTaskBlock && !isMemberDocument) {
    const getMemberLinkDisabledReason = () => {
      if (!document?.family) {
        return "Document must be assigned to a family";
      }
      if (
        document?.scope !== DocumentScope.FAMILY &&
        document?.scope !== DocumentScope.PUBLIC
      ) {
        return "Document must be shared with the family or public";
      }
      return null;
    };

    const disabledReason = getMemberLinkDisabledReason();

    additionalOptions.unshift({
      title: isMemberLinkEnabled ? (
        "Copy member link"
      ) : (
        <Box>
          <Typography variant="body" color="text.secondary">
            Copy member link
          </Typography>
          <Typography
            variant="bodySmall"
            color="text.tertiary"
            sx={{ display: "block" }}
          >
            {disabledReason}
          </Typography>
        </Box>
      ),
      onClick: () => copyMemberLinkHandler(),
      disabled: !isMemberLinkEnabled,
    });
  }

  const isAccessible =
    document?.scope === DocumentScope.INTERNAL_DISCOVERABLE ||
    document?.scope === DocumentScope.PUBLIC;
  const _content = () => {
    if (!document && errorCode === 410) {
      return <DeletedDocumentAlert />;
    } else if (!document && errorCode === 403) {
      return currentUser ? (
        <Alert
          severity="warning"
          icon={<FileWarning size={20} />}
          sx={{ borderRadius: "10px", alignItems: "center" }}
        >
          <AlertTitle>
            Sorry, you do not have permission to view this document
          </AlertTitle>
          If you believe this is a mistake, reach out to the owner and request
          that they update the document's settings.
        </Alert>
      ) : (
        <LoginRequiredAlert isMemberDocument={isMemberDocument} />
      );
    } else if (!document && errorCode === 404) {
      return (
        <Alert
          severity="warning"
          icon={<FileWarning size={20} />}
          sx={{ borderRadius: "10px" }}
        >
          <AlertTitle>Document not found</AlertTitle>
          This document may have been deleted or the link may be incorrect.
        </Alert>
      );
    } else if (!document && error && !loading) {
      return (
        <Alert
          severity="error"
          icon={<FileWarning size={20} />}
          sx={{ borderRadius: "10px" }}
        >
          <AlertTitle>Something went wrong</AlertTitle>
          We were unable to load this document. Please try again later.
        </Alert>
      );
    } else if (!document || loading) {
      return <Loading />;
    }
    return (
      <Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: isMobile ? "start" : "center",
            flexDirection: isMobile ? "column" : "row",
          }}
        >
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent:
                window.history.length > 1 && !isMemberDocument
                  ? "space-between"
                  : "flex-end",
              flexDirection: isMobile ? "column" : "row",
            }}
          >
            {/* Back button and breadcrumbs for regular documents */}
            {window.history.length > 1 && !isMemberDocument && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  width: isMobile ? "100%" : "auto",
                  justifyContent: isMobile ? "space-between" : "flex-start",
                }}
              >
                <Breadcrumbs breadcrumbs={breadcrumbs} />
                {isMobile && !isMemberDocument && (
                  <DocumentLabels
                    initialDocument={document}
                    editable={isEditing}
                  />
                )}
              </Box>
            )}

            {/* Breadcrumbs for member documents */}
            {isMemberDocument && (
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <IconButton
                    onClick={() =>
                      window.history.length > 1
                        ? navigate(-1)
                        : navigate("/documents")
                    }
                  >
                    <ArrowLeft size={20} stroke="#198282" />
                  </IconButton>
                  <Breadcrumbs breadcrumbs={breadcrumbs} />
                </Box>
                {isMobile && documentActionButtons(document, additionalOptions)}
              </Box>
            )}

            {!isMobile && documentActionButtons(document, additionalOptions)}
          </Box>

          {!isMemberDocument && (
            <BookmarkToTaskDialog
              familyRef={copyToFamily?.ref || ""}
              familyName={copyToFamily?.name || ""}
              open={!!copyToFamily}
              onClose={() => {
                setCopyToFamily(undefined);
              }}
              entry={document ? fromDocument(document) : null}
              onAction={onBookmarkHandler}
              singleSelection={true}
            />
          )}
        </Box>

        {!isMemberDocument && (
          <FlexBox
            justifyContent="space-between"
            alignItems="center"
            sx={{ minWidth: 0 }}
          >
            <DocumentBreadcrumbs
              document={document}
              isTaskblock={isTaskBlock}
            />
          </FlexBox>
        )}

        <_Wrapped
          document={document}
          error={error}
          initialContent={initialContent}
          contentRef={contentRef}
          editable={isEditing}
          isMemberDocument={isMemberDocument}
          getCurrentHash={getCurrentHash}
          onDocumentUpdate={onDocumentUpdate}
          labelContent={
            !isMemberDocument ? (
              <DocumentLabels initialDocument={document} editable={isEditing} />
            ) : undefined
          }
        />

        {(!isMemberDocument ||
          (isMemberDocument && document?.owner?.ref === contextUser.ref)) && (
          <ConfirmationDialog
            content={"Are you sure you want to remove this document?"}
            {...confirmState.dialogProps}
          />
        )}
      </Box>
    );
  };

  const pageContent = _content();

  if (isMemberDocument) {
    return (
      <GridPage
        sx={{
          maxWidth: "1000px",
          margin: isMobile ? "" : "auto",
          padding: isMobile ? "12px 12px" : "32px 48px",
          gap: "20px",
          paddingBottom: "0px",
        }}
      >
        {pageContent}
      </GridPage>
    );
  }

  const leftPanelContent = (
    <Box
      sx={{
        padding: isMobile ? "12px 12px" : "32px 48px",
        paddingBottom: "0px",
      }}
    >
      <PictureInPictureContentProvider
        contentRef={contentRef}
        onClose={refreshDocument}
        id={document?.ref ? `document-${document.ref}` : undefined}
      >
        <PipEnabledDocumentContent _content={_content} />
      </PictureInPictureContentProvider>
      {shareDialogOpen && (
        <AddPostDialog
          open={!!shareDialogOpen}
          onClose={() => setShareDialogOpen(null)}
          {...shareDialogOpen}
          onCreated={(p) => {
            navigate(`/community/${encodeURIComponent(p.ref || "")}`);
          }}
          prePostAction={async () => {
            if (!isAccessible) {
              await onScopeChange(DocumentScope.INTERNAL_DISCOVERABLE);
            }
          }}
          warningBox={
            !isAccessible && (
              <Alert severity="warning">
                <Typography variant="body" color="text.secondary">
                  By sharing this document, you will make it accessible and
                  discoverable by the advisor community.
                </Typography>
              </Alert>
            )
          }
        />
      )}
      <EmailComposeDialog
        closed={!emailComposeOpen}
        onClose={() => {
          setEmailComposeOpen(false);
          setEmailDraft(undefined);
        }}
        onSent={async () => {}}
        onRemove={() => {}}
        initialDraft={emailDraft}
      />
    </Box>
  );

  const allowComments =
    !isMemberDocument &&
    document &&
    !document.family?.ref &&
    !isListDocument(document);
  const rightPanelContent = allowComments ? (
    <MiniFeed
      entityType={ActivityEntityType.DOCUMENT_SHARED}
      entityRef={document.ref}
      hideHeader={true}
      onCommentsLoaded={setCommentCount}
    />
  ) : null;

  return (
    <SplitPanel
      leftPanel={leftPanelContent}
      rightPanel={rightPanelContent}
      rightPanelIcon={<MessagesSquare size={20} />}
      rightPanelTitle={`Comments (${commentCount})`}
      leftPanelMaxWidth="1000px"
      defaultCollapsed={!showComments}
    />
  );
}

// Component that safely uses PiP context when wrapped in provider
const PipEnabledDocumentContent = ({
  _content,
}: {
  _content: () => React.ReactNode;
}) => {
  const { setPipContentProps, PipButton } = usePictureInPictureContentContext();
  const isPipAvailable = isPictureInPictureAvailable();

  // Store PiP context globally so _Wrapped can access it
  useEffect(() => {
    if (typeof window !== "undefined") {
      (window as any).__pipContext = {
        setPipContentProps,
        PipButton,
        isPipAvailable,
      };
    }
  }, [setPipContentProps, PipButton, isPipAvailable]);

  // Clean up when unmounting
  useEffect(() => {
    return () => {
      if (typeof window !== "undefined") {
        (window as any).__pipContext = null;
      }
    };
  }, []);

  return <>{_content()}</>;
};
